import {
    Brain,
    Cog,
    Shield,
    FileCheck,
    Users,
    Database,
    Cloud,
    Code,
    Settings,
    Lock,
    BarChart3,
    Layers,
    RefreshCw,
    GitBranch,
    Server,
    Cpu,
    Globe,
    Target,
    AlertTriangle,
    Terminal,
    Box,
    Key,
    Smartphone,
    Layout,
    MessageSquare,
    FileText,
    CloudLightning,
    Container,
} from 'lucide-react';
import { ServiceData } from '../types/service';

export const servicesDe: Record<string, ServiceData> = {
    'ai-automation': {
        title: 'KI & Automatisierung',
        subtitle: 'Automatisierung soll Reichweite und Wirkung erhöhen – ohne Datenschutz, Qualität und Nachweisbarkeit zu gefährden.',
        heroImage: '/images/ai_automation_hero.png',
        contextDesc: 'In Enterprise- und regulierten Umfeldern ist der Unterschied zwischen „Demo" und „Betrieb" entscheidend: Datenqualität, Governance, Monitoring und klare Verantwortlichkeiten. Wir helfen Ihnen, KI-Projekte von der Idee bis zum stabilen Betrieb zu führen – konform mit dem EU AI Act.',
        operationalReality: [
            'Daten liegen verteilt (M365, SharePoint, ERP/CRM, E-Mail, Dateien), oft ohne klare Datenverantwortung',
            'Sicherheitsbedenken blockieren Innovation: „Dürfen wir das überhaupt in die Cloud laden?"',
            'Mangelnde KI-Literacy: Mitarbeiter wissen nicht, wie sie Prompts sicher und effektiv nutzen',
            'EU AI Act-Compliance: Unsicherheit über Risikoklassifizierung und Dokumentationspflichten',
        ],
        deliverables: [
            'AI Readiness Assessment & Data Inventory',
            'Secure AI Gateway (Azure OpenAI / Bedrock) mit PII-Filter',
            'RAG-Implementierung (Chat with your Data) auf eigenen Dokumenten',
            'AI Governance Framework & EU AI Act Gap Analysis',
            'Prompt Engineering Workshops & Guidelines',
            'Umsetzung als kontrollierte Workflows (nicht „Chat überall")',
            'Observability: Logging, Metriken, Feedback-Loops, Human-in-the-Loop wo nötig',
            'Betriebsmodell: Verantwortlichkeiten, Review-Cadence, Change-/Rollback-Prozess',
        ],
        capabilities: [
            { icon: Brain, title: 'AI Agents & Chatbots', desc: 'Kanalstrategie, Wissensgrenzen, Quellenbindung, Zugriffsscopes' },
            { icon: BarChart3, title: 'Predictive Analytics', desc: 'Datenpipeline, Feature-Definition, Drift-Checks, Validierungsregeln' },
            { icon: FileCheck, title: 'Dokumentenverarbeitung', desc: 'Klassifizierung, Extraktion, Validierung, Audit-Trail, Versionierung' },
            { icon: Cog, title: 'Integrationen', desc: 'API-/Webhook-Design, Queueing, Fehlerbehandlung, Retries, Rate Limits' },
            { icon: Shield, title: 'Datenkontrollen', desc: 'PII-Filter, Masking, Zugriffskontrollen, Aufbewahrung' },
            { icon: RefreshCw, title: 'Betriebsmodell', desc: 'Verantwortlichkeiten, Review-Cadence, Change-/Rollback-Prozess' },
        ],
        techStack: [
            { icon: Brain, name: 'OpenAI / Azure OpenAI' },
            { icon: MessageSquare, name: 'LangChain' },
            { icon: Database, name: 'Pinecone / Vector DB' },
            { icon: FileText, name: 'Azure Document Intel' },
            { icon: Cloud, name: 'Azure AI Services' },
            { icon: Code, name: 'Python' },
        ],
        timeline: [
            {
                phase: 'Use-Case & Data Readiness',
                weeks: '2–4 Wochen',
                items: ['Use-Case Backlog', 'Dateninventar', 'Risiko-/Kontrollkonzept', 'Erfolgskriterien'],
            },
            {
                phase: 'Pilot',
                weeks: '4–8 Wochen',
                items: ['Pilot-Workflow', 'Logging/Metriken', 'Validierungsregeln', 'Runbook'],
                validation: ['Testdatensätze', 'Fehlerraten', 'Menschliche Freigaben', 'Zugriffstests'],
            },
            {
                phase: 'Scale & Operations',
                weeks: '6–12 Wochen',
                items: ['Governance', 'SLOs/Monitoring', 'Change-/Rollback', 'Training', 'Ownership-Übergabe'],
            },
        ],
        riskCompliance: [
            { category: 'Datenschutz', items: ['Klare Datenquellen', 'Zweckbindung', 'Minimierung', 'Zugriffsscopes'] },
            { category: 'Qualität', items: ['Validierungsregeln', 'Stichprobenkontrollen', 'Drift-/Anomaliechecks'] },
            { category: 'Nachweise', items: ['Logs', 'Prompt-/Policy-Konfigurationen', 'Freigaben', 'Release Notes'] },
        ],
        enablement: [
            'Playbooks: Prompt-/Policy-Änderungen, Incident Handling, Data-Change Impact',
            'Training: Admin/Owner Training + Kurzleitfaden für Fachbereiche',
        ],
        nextSteps: [
            { title: 'Use-Case Screening', duration: '60 Min.', desc: '3–5 Kandidaten + Risikoeinschätzung' },
            { title: 'Pilot-Assessment', duration: '2–4 Wochen', desc: 'Daten-/Zugriffskonzept + Pilotplan' },
        ],
    },
    'erp-sap': {
        title: 'ERP & SAP',
        subtitle: 'Stabilisierung und Weiterentwicklung von ERP-Kernprozessen mit klarem Fokus auf Integration, Compliance und Betrieb.',
        heroImage: '/images/erp_sap_hero.png',
        contextDesc: 'ERP-Systeme sind in vielen Organisationen der Prozess- und Datenkern. Änderungen müssen stabil, nachvollziehbar und integriert erfolgen – besonders wenn Schnittstellen, Compliance und Betriebsanforderungen mitlaufen.',
        operationalReality: [
            'Legacy-Integrationen, individuelle Erweiterungen, lange Release-Zyklen',
            'Abhängigkeiten zu Logistik/Finanzen/Reporting, wenig Toleranz für Ausfälle',
            'Mehrere Umgebungen (DEV/QA/PRD), harte Cutover-Fenster, knappe Testzeit',
        ],
        deliverables: [
            'Stabilisierung und Weiterentwicklung von ERP-Kernprozessen',
            'Schnittstellen- und Integrationskatalog (Ownership, SLA, Fehlerpfade)',
            'Release- und Teststrategie (Quality Gates, Regression, Abnahme)',
            'Integration in Sicherheits-/Governance-Modelle (Zugriffe, Audit Trails, Changes)',
            'Dokumentation und Übergabe in den Betrieb',
        ],
        architectureBlueprint: {
            title: 'Standard Architecture Blueprint: AI Agent ↔ SAP',
            description:
                'Referenzarchitektur für sichere AI-Integration in SAP-Backends. Datenschutz, Governance und Audit-Trails sind von Beginn an eingebettet.',
            image: '/images/service_abstract_geometric.png',
            bullets: [
                'Privacy Proxy maskiert PII vor Agent-Calls',
                'SAP BTP/Integration Suite als kontrollierte Schnittstelle',
                'Audit Logs und Change Control für Transportwege',
            ],
        },
        capabilities: [
            { icon: Layers, title: 'SAP S/4HANA', desc: 'Prozessanalyse, Erweiterungen, Datenqualität, Transport-/Release-Disziplin' },
            { icon: Code, title: 'SAP Fiori Entwicklung', desc: 'Rollen/Apps, UX-Constraints, Berechtigungen, Deployment-Gates' },
            { icon: Cog, title: 'ERP Integration', desc: 'API-/EDI-Contracts, Mapping-Regeln, Fehlerhandling, Monitoring' },
            { icon: FileCheck, title: 'Evidence', desc: 'Change Logs, Transportlisten, Freigaben, Schnittstellenprotokolle' },
            { icon: Database, title: 'Data Migration', desc: 'Datenqualität, Mapping, Validierung, Cutover-Planung' },
            { icon: Shield, title: 'Governance', desc: 'Berechtigungskonzept, Audit-Trails, Compliance-Nachweise' },
        ],
        techStack: [
            { icon: Layers, name: 'SAP S/4HANA' },
            { icon: Layout, name: 'SAP Fiori' },
            { icon: Cloud, name: 'SAP BTP' },
            { icon: Cog, name: 'SAP Integration Suite' },
            { icon: Database, name: 'SAP HANA' },
            { icon: Code, name: 'ABAP' },
        ],
        timeline: [
            {
                phase: 'Assessment & Inventory',
                weeks: '2–4 Wochen',
                items: ['Prozess-/Schnittstelleninventar', 'Risiko-/Stabilitätsanalyse', 'Roadmap'],
            },
            {
                phase: 'Stabilize & Govern',
                weeks: '4–8 Wochen',
                items: ['Quality Gates', 'Teststrategie', 'Monitoring für kritische Flüsse', 'Rollen-/Zugriffs-Review'],
            },
            {
                phase: 'Implement & Release',
                weeks: 'Laufend, iterativ',
                items: ['Releases mit Abnahmeprotokoll', 'Schnittstellenverträge', 'Betriebsrunbooks'],
            },
        ],
        riskCompliance: [
            { category: 'Kontrollierte Changes', items: ['Definierte Freigaben', 'Klare Rollback-Kriterien', 'Dokumentierte Abnahmen'] },
            { category: 'Auditability', items: ['Nachvollziehbare Änderungen an Customizing/Code/Interfaces'] },
            { category: 'Datenschutz', items: ['Zweckbindung und Datenminimierung in Schnittstellenverträgen'] },
        ],
        enablement: [
            'Betriebsübergabe: Runbooks, Schnittstellen-Checklisten, Eskalationspfade',
            'Enablement: Training für Key User/Admins, Release- und Testprozesse',
        ],
        nextSteps: [
            { title: 'ERP/Interface Inventory Workshop', duration: '90 Min.', desc: 'Bestandsaufnahme Ihrer Systemlandschaft' },
            { title: 'Assessment', duration: '2–4 Wochen', desc: 'Inventar + Release-/Governance-Plan' },
        ],
    },
    'software-engineering': {
        title: 'Softwareentwicklung',
        subtitle: 'Maßgeschneiderte Softwarelösungen mit modernen Technologien und bewährten Engineering-Praktiken.',
        heroImage: '/images/software_engineering_hero.png',
        contextDesc: 'Software ist kein Selbstzweck – sie muss Geschäftsprobleme lösen, wartbar sein und sich in bestehende Landschaften integrieren. Wir liefern keine isolierten Features, sondern durchdachte Lösungen mit klarer Ownership und Betriebsfähigkeit.',
        operationalReality: [
            'Bestehende Systeme mit technischen Schulden und unklaren Verantwortlichkeiten',
            'Druck auf schnelle Lieferung bei gleichzeitig hohen Qualitätsanforderungen',
            'Fragmentierte Toolchains und fehlende Observability',
        ],
        deliverables: [
            'Requirements Engineering und Architekturdesign',
            'Entwicklung mit modernem Tech-Stack (React, TypeScript, Node.js, Cloud)',
            'CI/CD-Pipelines und automatisierte Tests',
            'API-Design und Integration in bestehende Systeme',
            'Dokumentation, Code Reviews und Wissenstransfer',
        ],
        capabilities: [
            { icon: Code, title: 'Frontend Development', desc: 'React, Vue, Angular mit TypeScript und modernen UI-Frameworks' },
            { icon: Server, title: 'Backend Development', desc: 'Node.js, Python, Go mit RESTful APIs und GraphQL' },
            { icon: Cloud, title: 'Cloud-Native', desc: 'Kubernetes, Serverless, Microservices-Architektur' },
            { icon: Database, title: 'Data Engineering', desc: 'SQL, NoSQL, Data Pipelines und ETL' },
            { icon: GitBranch, title: 'DevOps', desc: 'CI/CD, Infrastructure as Code, Container Orchestration' },
            { icon: Shield, title: 'Security', desc: 'SAST, DAST, Dependency Scanning, Security Reviews' },
        ],
        techStack: [
            { icon: Code, name: 'React / TypeScript' },
            { icon: Server, name: 'Node.js / NestJS' },
            { icon: Box, name: 'Docker' },
            { icon: Cloud, name: 'AWS / Azure' },
            { icon: Database, name: 'PostgreSQL' },
            { icon: Terminal, name: 'Terraform' },
        ],
        timeline: [
            {
                phase: 'Discovery & Architecture',
                weeks: '2–4 Wochen',
                items: ['Requirements', 'System Design', 'Tech-Stack Auswahl', 'Roadmap'],
            },
            {
                phase: 'Development Sprints',
                weeks: '8–16 Wochen',
                items: ['MVP', 'Iterative Releases', 'CI/CD Pipeline', 'Code Reviews'],
                validation: ['Automatisierte Tests', 'Manuelle QA', 'Security Scans', 'Performance Tests'],
            },
            {
                phase: 'Launch & Optimization',
                weeks: '2–4 Wochen',
                items: ['Production Deployment', 'Monitoring', 'Performance Tuning', 'Handover'],
            },
        ],
        riskCompliance: [
            { category: 'Code Quality', items: ['Linting', 'Code Reviews', 'Test Coverage', 'Technical Debt Management'] },
            { category: 'Security', items: ['OWASP Checks', 'Dependency Audits', 'Secret Management'] },
            { category: 'Compliance', items: ['Lizenz-Compliance', 'Accessibility', 'DSGVO-konforme Datenverarbeitung'] },
        ],
        enablement: [
            'Technische Dokumentation und Architecture Decision Records (ADRs)',
            'Pair Programming und Knowledge Sharing Sessions',
            'Runbooks für Deployment und Incident Handling',
        ],
        nextSteps: [
            { title: 'Discovery Workshop', duration: '2–4 Stunden', desc: 'Requirements und Architektur-Scoping' },
            { title: 'MVP Sprint', duration: '4–6 Wochen', desc: 'Proof of Concept mit echtem Mehrwert' },
        ],
    },
    'cloud-platform-foundations': {
        title: 'Cloud & Platform Foundations',
        subtitle: 'Sichere, skalierbare Cloud-Infrastruktur als Fundament für Ihre digitale Transformation.',
        heroImage: '/images/cloud_platform_hero.png',
        contextDesc: 'Cloud ist mehr als Infrastruktur – es ist ein Betriebsmodell. Wir helfen Ihnen, Landing Zones aufzubauen, Workloads zu migrieren und Teams zu befähigen, selbstständig und sicher in der Cloud zu arbeiten.',
        operationalReality: [
            'On-Premises-Altlasten mit unklar dokumentierten Abhängigkeiten',
            'Compliance-Anforderungen und Data-Residency-Vorgaben',
            'Teams mit unterschiedlichem Cloud-Reifegrad',
        ],
        deliverables: [
            'Cloud Readiness Assessment und Migrationsstrategie',
            'Landing Zone mit Security Baseline und Governance',
            'Infrastructure as Code Templates (Terraform, CloudFormation)',
            'Workload-Migration mit Validierung und Rollback-Planung',
            'Team Enablement und Self-Service-Befähigung',
        ],
        architectureBlueprint: {
            title: 'Standard Architecture Blueprint: Secure AI Platform',
            description:
                'Blueprint für eine auditierbare AI-Plattform in der Cloud mit klaren Data- und Security-Grenzen.',
            image: '/images/service_abstract_geometric.png',
            bullets: [
                'Identity & Policy Layer für kontrollierte Zugriffe',
                'Observability mit Security- und Audit-Logs',
                'Datenklassifizierung, Residency und Encryption by default',
            ],
        },
        capabilities: [
            { icon: Cloud, title: 'Cloud Strategy', desc: 'Assessment, Roadmap und Business Case für Cloud-Migration' },
            { icon: Server, title: 'Landing Zone', desc: 'Sichere, skalierbare Grundstruktur für Ihre Cloud-Workloads' },
            { icon: Lock, title: 'Identity & Access', desc: 'IAM, SSO, MFA, Privileged Access Management' },
            { icon: Globe, title: 'Networking', desc: 'VPC, Peering, Transit Gateway, CDN, DNS' },
            { icon: Shield, title: 'Security & Compliance', desc: 'Security Hub, Config Rules, Audit Logging' },
            { icon: Cpu, title: 'Container & Kubernetes', desc: 'EKS, AKS, GKE – Orchestration für moderne Workloads' },
        ],
        techStack: [
            { icon: Cloud, name: 'AWS' },
            { icon: CloudLightning, name: 'Azure' },
            { icon: Terminal, name: 'Terraform' },
            { icon: Container, name: 'Kubernetes' },
            { icon: GitBranch, name: 'GitHub Actions' },
            { icon: Shield, name: 'HashiCorp Vault' },
        ],
        timeline: [
            {
                phase: 'Assessment & Strategy',
                weeks: '2–3 Wochen',
                items: ['Cloud Readiness', 'Migration Plan', 'Cost Analysis', 'Risk Assessment'],
            },
            {
                phase: 'Infrastructure Setup',
                weeks: '4–8 Wochen',
                items: ['IaC Templates', 'Security Baseline', 'Networking', 'Monitoring'],
            },
            {
                phase: 'Migration & Enablement',
                weeks: '4–12 Wochen',
                items: ['Workload Migration', 'Team Training', 'Operations Handover', 'Documentation'],
                validation: ['Functional Tests', 'Performance Benchmarks', 'Security Scans'],
            },
        ],
        riskCompliance: [
            { category: 'Security', items: ['Network Segmentation', 'Encryption at Rest/Transit', 'WAF/DDoS Protection'] },
            { category: 'Compliance', items: ['Data Residency', 'Audit Logging', 'Retention Policies'] },
            { category: 'Cost Management', items: ['Budgets & Alerts', 'Reserved Instances', 'Right-Sizing'] },
        ],
        enablement: [
            'Cloud-native Training für Entwickler und Ops-Teams',
            'Self-Service Playbooks für gängige Szenarien',
            'FinOps Awareness und Cost Optimization Workshops',
        ],
        nextSteps: [
            { title: 'Cloud Readiness Assessment', duration: '1–2 Wochen', desc: 'Inventar und Migrationskandidaten' },
            { title: 'Landing Zone Pilot', duration: '4–6 Wochen', desc: 'Erste Workload in sicherer Umgebung' },
        ],
    },
    'change-management-delivery': {
        title: 'Change Management & Delivery',
        subtitle: 'Sicherung der Business Continuity und Risikominimierung bei komplexen IT-Carve-outs und Transformationen.',
        heroImage: '/images/change_management_hero.png',
        contextDesc: 'Die Entflechtung gewachsener IT-Strukturen stellt ein signifikantes operatives Risiko dar. Ohne stringentes Management drohen Systemausfälle und Prozessunterbrechungen, die den Geschäftsbetrieb gefährden. Wir verstehen Change Management als Risikomanagement: Durch klare Governance, präzise Schnittstellenanalysen und strukturierte Migrationspläne sichern wir die Business Continuity. Unser Ziel ist die Überführung in eine autonome, zukunftsfähige IT-Landschaft bei minimaler Disruption des Tagesgeschäfts. Strukturierter Change reduziert Nacharbeiten, Produktivitätsverluste und operative Störungen signifikant und ist ein wesentlicher Faktor zur Einhaltung von Zeit- und Budgetzielen.',
        realityTag: 'Operational Impact',
        riskTag: 'Risk Reduction',
        benefitsTag: 'Business Value',
        operationalReality: [
            'Kleine interne IT-Abteilung mit begrenzten Ressourcen für komplexe Carve-out-Projekte',
            'Verwobene IT-Systeme mit unklaren Abhängigkeiten und Schnittstellen zur Muttergesellschaft',
            'Hoher Zeit- und Budgetdruck bei gleichzeitiger Notwendigkeit der Betriebsstabilität',
            'Veraltete Prozesse erfordern Modernisierung und Neuausrichtung der IT-Landschaft',
        ],
        deliverables: [
            'IT-Autonomie: Vollständig eigenständige Infrastruktur und Identitätsverwaltung',
            'Betriebsstabilität: Sicherer, skalierbarer Betrieb nach Abschluss des Carve-outs',
            'Digitalisierung als Chance: Nutzung der Transformation zur Modernisierung veralteter Systeme',
            'Cloud-native Architekturen und automatisierte Prozesse',
            'Definierte Service-Level und dokumentierte Prozesse',
        ],
        capabilities: [
            { icon: Users, title: 'Steering Committee', desc: 'Monatliche Meetings, strategische Entscheidungen, Budget-Freigaben, Eskalationsinstanz' },
            { icon: Target, title: 'Projektleitung', desc: 'Wöchentliche Status-Meetings, Koordination aller Workstreams, Risiko-Management' },
            { icon: Settings, title: 'Fachteams', desc: 'Dedizierte Teams für Infrastruktur, Applikationen, Security. Tägliche Stand-ups' },
            { icon: MessageSquare, title: 'Kommunikation', desc: 'Wöchentliche Status-Reports, Executive Summaries, dediziertes Projektportal' },
            { icon: Shield, title: 'Risikomanagement', desc: 'Strukturiertes Risikomanagement nach PMI-Standard, wöchentliche Reviews' },
            { icon: FileCheck, title: 'Qualitätssicherung', desc: 'ISO 9001-zertifizierte Prozesse, Vier-Augen-Prinzip, automatisierte Tests' },
        ],
        techStack: [
            {
                icon: Users,
                name: 'Miro / Mural',
                desc: 'Strukturierte Beteiligung & gemeinsame Entscheidungsfindung. Miro unterstützt die aktive Einbindung von Stakeholdern in allen Phasen der Transformation.',
                benefits: [
                    'Gemeinsames Verständnis von Zielbild, Risiken und Auswirkungen',
                    'Strukturierte Workshops statt unkontrollierter Diskussionen',
                    'Nachvollziehbare Entscheidungsgrundlagen für Management & PMO',
                ],
            },
            {
                icon: Layout,
                name: 'Jira / Confluence',
                desc: 'Transparenz über Fortschritt, Risiken und Abhängigkeiten. Jira ermöglicht eine faktenbasierte Steuerung von Change-Maßnahmen.',
                benefits: [
                    'Klare Nachverfolgbarkeit von Change-Maßnahmen',
                    'Frühzeitige Identifikation von Engpässen und Eskalationen',
                    'Verlässliche Grundlage für Status-Reports und Steering Committees',
                ],
            },
            {
                icon: MessageSquare,
                name: 'Microsoft Teams',
                desc: 'Verlässliche Kommunikation in der Transformation. Teams ist der zentrale Kanal für strukturierte, konsistente und zielgruppengerechte Information.',
                benefits: [
                    'Reduktion von Gerüchten und Informationslücken',
                    'Klare Kommunikationsformate für Führungskräfte und Mitarbeitende',
                    'Direkte Ansprechbarkeit während kritischer Projektphasen',
                ],
            },
            {
                icon: FileText,
                name: 'SharePoint',
                desc: 'Verbindliche Informationsquelle & Wissensbasis. SharePoint dient als Single Source of Truth für alle change-relevanten Informationen.',
                benefits: [
                    'Klare Orientierung für alle Beteiligten',
                    'Vermeidung von Informationsverlusten',
                    'Nachhaltige Übergabe in den operativen Betrieb',
                ],
            },
            {
                icon: BarChart3,
                name: 'Power BI',
                desc: 'Steuerungsfähigkeit durch messbare Veränderung. Power BI übersetzt Veränderung in steuerbare Kennzahlen.',
                benefits: [
                    'Objektive Entscheidungsgrundlagen für Management',
                    'Transparenz über Projektfortschritt und Risiken',
                    'Frühwarnsystem für Abweichungen von Zeit- oder Zielvorgaben',
                ],
            },
        ],
        timeline: [
            {
                phase: 'Baselining & IT-Strategie',
                weeks: '6–8 Wochen',
                items: ['Bestandsaufnahme', 'Abhängigkeitsanalyse', 'Zielarchitektur', 'Roadmap-Entwicklung'],
            },
            {
                phase: 'Infrastruktur-Setup',
                weeks: '8–12 Wochen',
                items: ['Aufbau neuer Tenants', 'Cloud-Umgebungen', 'Netzwerk', 'Identity & Access Management'],
            },
            {
                phase: 'Migration & Trennung',
                weeks: '12–16 Wochen',
                items: ['Datenmigration', 'Applikationsumzug', 'Systemtrennung', 'Schnittstellenanpassung'],
            },
            {
                phase: 'Testing & Stabilisierung',
                weeks: '6–8 Wochen',
                items: ['Integrationstests', 'Sicherheitsprüfungen', 'Performance-Optimierung', 'Dokumentation'],
            },
            {
                phase: 'Go-Live & Hypercare',
                weeks: '4–6 Wochen',
                items: ['Produktivsetzung', 'Intensives Monitoring', 'Incident-Management', 'Wissenstransfer'],
            },
        ],
        riskCompliance: [
            { category: 'Risikominimierung', items: ['Strukturiertes Risikomanagement (PMI)', 'Wöchentliche Reviews', 'Definierte Eskalationspfade'] },
            { category: 'Qualitätssicherung', items: ['ISO 9001-Prozesse', 'Vier-Augen-Prinzip', 'Abnahmekriterien für Meilensteine'] },
            { category: 'Security & Compliance', items: ['ISO 27001-konform', 'DSGVO-Compliance', 'Verschlüsselung sensibler Daten', 'Audit-Trails'] },
        ],
        enablement: [
            'Schulungen für IT-Mitarbeiter und Übergabe von Dokumentation/Runbooks',
            'Shadowing und Hands-on-Training für das interne IT-Team',
            'Etablierung von Support-Prozessen und Retained-Support-Vereinbarungen',
        ],
        detailedEnablement: [
            {
                title: 'Strukturierter Wissenstransfer',
                desc: 'Wissen wird nicht implizit vorausgesetzt, sondern gezielt und nachvollziehbar aufgebaut. Alle relevanten technischen, organisatorischen und betrieblichen Inhalte werden systematisch an das interne IT-Team übergeben.',
                items: [
                    'Übergabe vollständiger System- und Betriebsdokumentation',
                    'Runbooks, Notfall- und Eskalationsprozesse',
                    'Architektur- und Entscheidungsdokumentation',
                ],
            },
            {
                title: 'Befähigung der internen IT-Organisation',
                desc: 'Durch praxisnahe Schulungen und begleitende Zusammenarbeit wird das interne Team befähigt, Systeme sicher zu betreiben, weiterzuentwickeln und zukünftige Veränderungen selbstständig umzusetzen.',
                items: [
                    'Rollenbezogene Trainings für IT, Betrieb und Fachbereiche',
                    'Shadowing und Hands-on-Training im Projektverlauf',
                    'Klare Verantwortlichkeiten für Betrieb und Weiterentwicklung',
                ],
            },
            {
                title: 'Übergang in den stabilen Betrieb',
                desc: 'Die Übergabe erfolgt nicht punktuell, sondern strukturiert und abgesichert. So wird gewährleistet, dass der operative Betrieb nach Projektabschluss stabil, transparent und skalierbar fortgeführt werden kann.',
                items: [
                    'Definierte Übergabe-Meilensteine',
                    'Abnahme durch Fachbereiche und IT',
                    'Etablierte Support- und Betriebsprozesse',
                ],
            },
            {
                title: 'Optionale Unterstützung ohne Abhängigkeit',
                desc: 'Bei Bedarf begleiten wir den Übergang mit zeitlich begrenztem Retained Support. Ziel ist nicht dauerhafte Bindung, sondern kontrollierte Stabilisierung und schrittweiser Rückzug.',
                items: [
                    'Zeitlich klar begrenzte Support-Vereinbarungen',
                    'Wissenstransfer statt Ticket-Abarbeitung',
                    'Planbarer Exit externer Unterstützung',
                ],
            },
        ],
        benefits: [
            { title: 'Executive Management', desc: 'Maximale Risikominimierung bei Carve-outs, garantierte Business Continuity und revisionssichere Compliance (ISO/DSGVO).' },
            { title: 'Line Managers', desc: 'Operative Stabilität während der Transition, klare Eskalationspfade und entlastete Teams durch strukturierte Hypercare.' },
            { title: 'Employees', desc: 'Planungssicherheit durch transparente Kommunikation, gezieltes Enablement und frustfreie Adoption neuer Systeme.' },
        ],
        nextSteps: [
            { title: 'Baselining Workshop', duration: '1 Tag', desc: 'Initiale Bestandsaufnahme und Scope-Definition' },
            { title: 'IT-Strategie Assessment', duration: '2–4 Wochen', desc: 'Detaillierte Analyse und Roadmap-Entwicklung' },
        ],
    },
    'security-privacy-governance': {
        title: 'Security, Privacy & Governance',
        subtitle: 'Sicherheit, Datenschutz und Compliance als integraler Bestandteil Ihrer digitalen Prozesse.',
        heroImage: '/images/security_governance_hero.png',
        contextDesc: 'Sicherheit ist kein einmaliges Projekt, sondern ein kontinuierlicher Prozess. Für Unternehmen und öffentliche Träger ist das Vertrauen von Kunden und Partnern ein Kernwert. Wir etablieren pragmatische ISMS-Strukturen, die Sie schützen, ohne Delivery zu bremsen – und machen Sie fit für NIS2.',
        operationalReality: [
            'Zunehmende Cyber-Bedrohungen (Ransomware, Phishing) zielen auf kritische Prozesse und Lieferketten',
            'Begrenzte Budgets und Personal für dedizierte Security-Teams',
            'Komplexe Compliance-Anforderungen (DSGVO, Spenden-Siegel, NIS2) bei wenig Expertise',
        ],
        deliverables: [
            'Security Assessment & Roadmap (NIS2 Readiness Check)',
            'Virtual CISO (vCISO) Service',
            'Identity & Access Management (IAM) Modernisierung',
            'Cloud Security Posture Management (CSPM)',
            'Awareness-Kampagnen & Phishing-Simulationen',
        ],
        capabilities: [
            { icon: Lock, title: 'Identity & Access', desc: 'MFA, Conditional Access, Privileged Access Management' },
            { icon: Shield, title: 'Endpoint Security', desc: 'EDR, MDM, Device Compliance und Patch Management' },
            { icon: FileCheck, title: 'Data Protection', desc: 'DLP, Klassifizierung, Verschlüsselung und Backup' },
            { icon: AlertTriangle, title: 'Incident Response', desc: 'Detection, Playbooks und koordinierte Reaktion' },
            { icon: BarChart3, title: 'Risk Management', desc: 'Risikobewertung, Behandlung und Monitoring' },
            { icon: Settings, title: 'Compliance', desc: 'ISO 27001, DSGVO, SOC 2, NIS2 und branchenspezifische Standards' },
        ],
        techStack: [
            { icon: Key, name: 'Azure AD / Entra ID' },
            { icon: Shield, name: 'Microsoft Sentinel' },
            { icon: Lock, name: 'CrowdStrike' },
            { icon: Smartphone, name: 'Intune MDM' },
            { icon: FileCheck, name: 'OneTrust' },
            { icon: AlertTriangle, name: 'Splunk' },
        ],
        timeline: [
            {
                phase: 'Security Assessment',
                weeks: '2-4 Wochen',
                items: ['Risk Register', 'Vulnerability Report', 'NIS2 Gap Analysis', 'Roadmap'],
                validation: ['Management-Präsentation der Risiken'],
            },
            {
                phase: 'Foundation & Quick Wins',
                weeks: '4-8 Wochen',
                items: ['MFA Rollout', 'Endpoint Protection', 'Backup-Review', 'Incident Response Plan'],
                validation: ['Security Score Verbesserung'],
            },
            {
                phase: 'ISMS & Governance',
                weeks: '3-6 Monate',
                items: ['Richtlinienerstellung', 'Prozessdefinition', 'Mitarbeiterschulung', 'Audit-Vorbereitung'],
                validation: ['Internes Audit / Zertifizierung'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['DSGVO', 'ISO 27001', 'BSI IT-Grundschutz', 'NIS2'] },
            { category: 'Operational', items: ['Business Continuity', 'Disaster Recovery', 'Supply Chain Security'] },
        ],
        enablement: [
            'Security Champions Programm',
            'Regelmäßige Phishing-Simulationen',
            'Tabletop Exercises für das Management',
        ],
        nextSteps: [
            { title: 'Security Quick-Check vereinbaren', duration: '2 Tage', desc: 'Gap-Analyse und Top-Risiken' },
            { title: 'NIS2 Betroffenheitsanalyse', duration: '1 Woche', desc: 'Priorisierung und Maßnahmenplanung' },
        ],
    },
    'sustainability-esg-tech': {
        title: 'Sustainability & ESG Tech',
        subtitle: 'Verwandeln Sie die CSRD-Pflicht in einen Datenvorteil. Automatisierte ESG-Berichterstattung statt Excel-Chaos.',
        heroImage: '/images/sustainability_esg_hero.png',
        contextDesc: 'Die Corporate Sustainability Reporting Directive (CSRD) zwingt Unternehmen, Nachhaltigkeitsdaten so rigoros wie Finanzdaten zu berichten. Manuelle Prozesse skalieren hier nicht mehr. Wir bauen die Daten-Infrastruktur für Ihre Nachhaltigkeitsziele.',
        operationalReality: [
            'Datenchaos: ESG-Daten liegen in Silos (Stromrechnungen, Reisekosten, Produktion)',
            'Manuelle Fehleranfälligkeit: Excel-basierte CO2-Berechnungen sind nicht audit-sicher',
            'Steigender Druck: Banken und Kunden verlangen verlässliche ESG-Nachweise',
            'Fehlende Integration: Nachhaltigkeit ist nicht in die Kernprozesse (ERP) integriert',
        ],
        deliverables: [
            'ESG Data Strategy & Architecture',
            'Automated Carbon Footprint Calculation (Scope 1, 2, 3)',
            'CSRD Reporting Platform Implementation',
            'Supply Chain Transparency (LkSG)',
            'Green IT & Cloud Optimization',
        ],
        capabilities: [
            { icon: FileCheck, title: 'Data Collection', desc: 'Automatisierte Konnektoren zu ERP, Facility Management und Reisekostensystemen.' },
            { icon: BarChart3, title: 'Calculation Engine', desc: 'Auditsichere Berechnung von Emissionen nach GHG Protocol.' },
            { icon: Layout, title: 'Reporting Hub', desc: 'Zentrale Plattform für CSRD, EU Taxonomie und LkSG Berichte.' },
            { icon: Users, title: 'Supplier Portal', desc: 'Digitale Erfassung von Lieferantendaten für Scope 3.' },
            { icon: FileCheck, title: 'Audit Trail', desc: 'Lückenlose Nachvollziehbarkeit jeder Datenänderung für den Wirtschaftsprüfer.' },
            { icon: BarChart3, title: 'Dashboarding', desc: 'Echtzeit-KPIs für das Management zur Steuerung der Nachhaltigkeitsziele.' },
        ],
        techStack: [
            { icon: Cloud, name: 'Microsoft Sustainability Manager' },
            { icon: Database, name: 'SAP Sustainability Control Tower' },
            { icon: BarChart3, name: 'Power BI' },
            { icon: Code, name: 'Python / Pandas' },
            { icon: Server, name: 'Azure Data Lake' },
            { icon: FileText, name: 'Workiva' },
        ],
        timeline: [
            {
                phase: 'Discovery & Materiality',
                weeks: '4-6 Wochen',
                items: ['Wesentlichkeitsanalyse', 'Datenquellen-Inventar', 'Architektur-Entwurf'],
                validation: ['Scope-Definition'],
            },
            {
                phase: 'Data Foundation',
                weeks: '8-12 Wochen',
                items: ['Data Lake Setup', 'Konnektorenimplementierung', 'Datenbereinigung'],
                validation: ['Data Quality Check'],
            },
            {
                phase: 'Reporting & Dashboarding',
                weeks: '6-8 Wochen',
                items: ['CSRD-Berichtslayout', 'Management-Dashboards', 'Audit-Vorbereitung'],
                validation: ['Test-Bericht'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['CSRD', 'EU Taxonomie', 'LkSG', 'GHG Protocol'] },
            { category: 'Data', items: ['Data Integrity', 'Auditability', 'Third-Party Data Risk'] },
        ],
        enablement: [
            'Schulung der Fachbereiche zur Dateneingabe',
            'Handover an das Sustainability Office',
            'Regelmäßige Data Quality Reviews',
        ],
        nextSteps: [
            { title: 'ESG Data Readiness Check', duration: '1 Woche', desc: 'Inventar und Migrationskandidaten' },
            { title: 'CSRD Gap Analyse', duration: '2-3 Wochen', desc: 'Priorisierung und Maßnahmenplanung' },
        ],
    },
    'strategy-and-transformation': {
        title: 'Strategie & Transformation',
        subtitle: 'Erstellen Sie eine messbare Transformations-Roadmap mit klarer Governance, Investitionsprioritäten und einem Ausführungsplan, den Teams tatsächlich umsetzen können.',
        heroImage: '/images/change_management_hero.png',
        contextDesc: 'Moderne Transformationen scheitern selten an der Technologie – sondern meist daran, dass Prioritäten, Governance und Ausführungsdisziplin nicht aufeinander abgestimmt sind. Wir helfen Führungsteams dabei, eine klare Richtung vorzugeben, Kostentransparenz über Portfolios und Anbieter hinweg zu schaffen und die Strategie in einen ausführbaren Plan mit Governance, KPIs und einem operativen Liefermodell zu übersetzen. Das Ergebnis sind schnellere Entscheidungen, reduzierte Risiken und messbare Geschäftsergebnisse.',
        realityTag: 'Operative Auswirkungen',
        riskTag: 'Risikominimierung',
        benefitsTag: 'Geschäftswert',
        operationalReality: [
            'Zu viele Initiativen, unklare Prioritäten und "alles ist dringend"',
            'Begrenzte Kostentransparenz über Apps, Anbieter und Lizenzen hinweg',
            'Schwache Governance: langsame Entscheidungen, unklare Verantwortlichkeiten, fragmentiertes Reporting',
            'Veränderungsmüdigkeit: geringe Akzeptanz, Widerstand und nicht realisierte Vorteile',
        ],
        deliverables: [
            'Enterprise Roadmap (12–18 Monate): Priorisierung, Sequenzierung, Meilensteine, Abhängigkeiten und Wertziele.',
            'Portfolio- & Kostenbasis: Transparenz über Apps, Anbieter und Lizenzierung.',
            'Operating Model & Governance: Klare Entscheidungsrechte und Verantwortlichkeiten für KPIs.',
            'Transformations-PMO-Setup: Lieferpläne, RAID-Logs und Reporting-Automatisierung.',
            'Change Enablement: Stakeholder-Mapping und Adoptionsmetriken.',
        ],
        capabilities: [
            { icon: Users, title: 'Lenkungsausschuss', desc: 'Entscheidungsrhythmus, Genehmigungen, Eskalationen, Ergebnisverfolgung.' },
            { icon: Target, title: 'Programm-Management', desc: 'Workstreams, Meilensteine, Abhängigkeiten, Risikomanagement.' },
            { icon: Settings, title: 'Architektur- & Lieferteams', desc: 'Zielarchitektur, Lieferstandards, Quality Gates.' },
            { icon: MessageSquare, title: 'Kommunikation & Alignment', desc: 'Updates, Entscheidungsprotokolle, Stakeholder-Kommunikation, Transparenz.' },
            { icon: Shield, title: 'Risikomanagement', desc: 'RAID-Disziplin, Minderungspläne, Problemlösung.' },
            { icon: FileCheck, title: 'Qualitätssicherung', desc: 'Abnahmekriterien, Governance-Gates, Dokumentationsqualität.' },
        ],
        capabilitiesTitle: 'Technische Bausteine',
        deliverablesTitle: 'Unsere Leistungen',
        techStack: [
            {
                icon: Users,
                name: 'Miro / Mural',
                desc: 'Strukturierte Beteiligung & gemeinsame Entscheidungsfindung. Miro unterstützt die aktive Einbindung von Stakeholdern in allen Phasen der Transformation.',
                benefits: [
                    'Gemeinsames Verständnis von Zielzustand, Risiken und Auswirkungen',
                    'Strukturierte Workshops statt unkontrollierter Diskussionen',
                    'Nachvollziehbare Entscheidungsgrundlage für Management & PMO',
                ],
            },
            {
                icon: Layout,
                name: 'Jira / Confluence',
                desc: 'Transparenz über Fortschritt, Risiken und Abhängigkeiten. Jira ermöglicht eine faktenbasierte Steuerung von Change-Maßnahmen.',
                benefits: [
                    'Klare Rückverfolgbarkeit von Change-Maßnahmen',
                    'Frühzeitige Identifikation von Engpässen und Eskalationen',
                    'Verlässliche Basis für Statusberichte und Lenkungsausschüsse',
                ],
            },
            {
                icon: MessageSquare,
                name: 'Microsoft Teams',
                desc: 'Verlässliche Kommunikation während der Transformation. Teams ist der zentrale Kanal für strukturierte, konsistente und zielgerichtete Informationen.',
                benefits: [
                    'Reduzierung von Gerüchten und Informationslücken',
                    'Klare Kommunikationsformate für Führungskräfte und Mitarbeitende',
                    'Direkte Erreichbarkeit während kritischer Projektphasen',
                ],
            },
            {
                icon: FileText,
                name: 'SharePoint',
                desc: 'Verbindliche Informationsquelle & Wissensbasis. SharePoint dient als Single Source of Truth für alle change-relevanten Informationen.',
                benefits: [
                    'Klare Orientierung für alle Beteiligten',
                    'Vermeidung von Informationsverlust',
                    'Nachhaltige Übergabe an den operativen Bereich',
                ],
            },
            {
                icon: BarChart3,
                name: 'Power BI',
                desc: 'Steuerbarkeit durch messbare Veränderung. Power BI übersetzt Veränderung in steuerbare KPIs.',
                benefits: [
                    'Objektive Entscheidungsgrundlage für das Management',
                    'Transparenz über Projektfortschritt und Risiken',
                    'Frühwarnsystem für Abweichungen von Zeit oder Zielen',
                ],
            },
        ],
        timeline: [
            {
                phase: 'Baseline & Strategische Ausrichtung',
                weeks: '2–4 Wochen',
                items: ['Portfolio-Inventar', 'Ist-Zustands-Bewertung', 'Transformations-Charter', 'Werthypothesen', 'RAID-Log'],
            },
            {
                phase: 'Ziel-Betriebsmodell & Governance',
                weeks: '2–4 Wochen',
                items: ['TOM-Optionen', 'RACI', 'Governance-Playbook', 'KPI/OKR-Framework', 'Reporting-Vorlagen'],
            },
            {
                phase: 'Roadmap & Investitionsplan',
                weeks: '3–6 Wochen',
                items: ['12–18 Monats-Roadmap', 'Sequencing & Abhängigkeiten', 'Investitionsrahmen', 'Ressourcenplan', 'Anbieterplan'],
            },
            {
                phase: 'Programm-Mobilisierung',
                weeks: '2–4 Wochen',
                items: ['Programmplan', 'Workstreams', 'Lieferrhythmus', 'Steering-Pack', 'Dashboards', 'Abnahmekriterien'],
            },
            {
                phase: 'Befähigung & Adoption',
                weeks: 'laufend / 4–8 Wochen Aufbau',
                items: ['Stakeholder-Map', 'Kommunikationsplan', 'Schulungskonzept', 'Adoptions-KPIs', 'Change-Champions-Modell'],
            },
        ],
        riskCompliance: [
            { category: 'Risikominimierung', items: ['RAID-Disziplin', 'Eskalationspfade', 'wöchentliche Reviews', 'Entscheidungsprotokolle'] },
            { category: 'Qualitätssicherung', items: ['Abnahmekriterien', 'Quality Gates', 'Dokumentationsstandards'] },
            { category: 'Sicherheit & Datenschutz', items: ['DSGVO-konforme Prozesse', 'audit-sichere Dokumentation', 'Zugriffskontrollen für Programmassets'] },
        ],
        enablement: [
            'Schulung der IT-Mitarbeiter und Übergabe von Dokumentation/Runbooks',
            'Shadowing und Praxis-Training für das interne IT-Team',
            'Etablierung von Support-Prozessen und Support-Vereinbarungen',
        ],
        detailedEnablement: [
            {
                title: 'Strukturierter Wissenstransfer',
                desc: 'Wissen wird nicht einfach vorausgesetzt, sondern gezielt und nachvollziehbar aufgebaut. Alle relevanten technischen, organisatorischen und betrieblichen Inhalte werden systematisch an das interne IT-Team übergeben.',
                items: [
                    'Übergabe vollständiger System- und Betriebsdokumentation',
                    'Runbooks, Notfall- und Eskalationsprozesse',
                    'Architektur- und Entscheidungsdokumentation',
                ],
            },
            {
                title: 'Befähigung der internen IT-Organisation',
                desc: 'Durch praxisnahe Schulungen und begleitende Zusammenarbeit wird das interne Team befähigt, Systeme sicher zu betreiben, weiterzuentwickeln und zukünftige Veränderungen selbstständig umzusetzen.',
                items: [
                    'Rollenbezogene Trainings für IT, Betrieb und Fachbereiche',
                    'Shadowing und Hands-on-Training im Projektverlauf',
                    'Klare Verantwortlichkeiten für Betrieb und Weiterentwicklung',
                ],
            },
            {
                title: 'Übergang in den stabilen Betrieb',
                desc: 'Die Übergabe erfolgt nicht punktuell, sondern strukturiert und abgesichert. So wird gewährleistet, dass der operative Betrieb nach Projektabschluss stabil, transparent und skalierbar fortgeführt werden kann.',
                items: [
                    'Definierte Übergabe-Meilensteine',
                    'Abnahme durch Fachbereiche und IT',
                    'Etablierte Support- und Betriebsprozesse',
                ],
            },
            {
                title: 'Optionale Unterstützung ohne Abhängigkeit',
                desc: 'Bei Bedarf begleiten wir den Übergang mit zeitlich begrenztem Retained Support. Ziel ist nicht dauerhafte Bindung, sondern kontrollierte Stabilisierung und schrittweiser Rückzug.',
                items: [
                    'Zeitlich klar begrenzte Support-Vereinbarungen',
                    'Wissenstransfer statt Ticket-Abarbeitung',
                    'Planbarer Exit externer Unterstützung',
                ],
            },
        ],
        benefits: [
            { title: 'Executive Management', desc: 'Klare Prioritäten, Investitionsplan und Governance – mit managementgerechtem Reporting und Entscheidungsvorlagen.' },
            { title: 'Transformations-Leiter / PMO', desc: 'Ein strukturierter Programmplan, RAID-Management, Abhängigkeiten und ein Lieferrhythmus, den Teams tatsächlich umsetzen können.' },
            { title: 'Fachbereichs- & IT-Leiter', desc: 'Abgestimmtes Betriebsmodell, definierte KPIs/OKRs und funktionsübergreifende Ausführung mit minimalen Reibungsverlusten.' },
        ],
        nextSteps: [
            { title: 'Baselining-Workshop', duration: '1 Tag', desc: 'Initiale Bestandsaufnahme und Scope-Definition' },
            { title: 'Strategie-Assessment', duration: '2–4 Wochen', desc: 'Detaillierte Analyse und Roadmap-Entwicklung' },
        ],
    },
};
